/**
 * services/api.js — API client for the backend.
 *
 * All API calls go through this module. Handles:
 * - JWT access token injection
 * - Automatic refresh token rotation on 401
 * - Error normalization
 * - ETag support for reduced polling
 *
 * Phase 2: Added refresh token management, auto-refresh on 401,
 * logout endpoint, change-password endpoint.
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

let _token = null;
let _refreshToken = null;
const _etags = {};

// ── Token management ──────────────────────────────────────────────────

export function setToken(token) {
  _token = token;
}

export function getToken() {
  return _token;
}

export function clearToken() {
  _token = null;
}

export function setRefreshToken(token) {
  _refreshToken = token;
}

export function getRefreshToken() {
  return _refreshToken;
}

export function clearRefreshToken() {
  _refreshToken = null;
}

// ── Refresh token handling ────────────────────────────────────────────

let _refreshing = null;

/**
 * Attempt to refresh the access token using the stored refresh token.
 * De-duplicates concurrent refresh attempts (only one in-flight at a time).
 * On success, updates both in-memory tokens and sessionStorage.
 * Returns the new token data on success, false on failure.
 */
async function tryRefresh() {
  if (!_refreshToken) return false;
  if (_refreshing) return _refreshing;

  _refreshing = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: _refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      _token = data.access_token;
      _refreshToken = data.refresh_token;
      // Sync to sessionStorage for persistence across page refreshes
      sessionStorage.setItem('rently_token', data.access_token);
      if (data.refresh_token) {
        sessionStorage.setItem('rently_refresh_token', data.refresh_token);
      }
      return data;
    } catch {
      return false;
    } finally {
      _refreshing = null;
    }
  })();

  return _refreshing;
}

// ── Core request function ─────────────────────────────────────────────

async function request(path, options = {}, _isRetry = false) {
  const url = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (_token) {
    headers['Authorization'] = `Bearer ${_token}`;
  }

  // ETag support for GET requests
  if (options.method === undefined || options.method === 'GET') {
    const cachedEtag = _etags[path];
    if (cachedEtag) {
      headers['If-None-Match'] = cachedEtag;
    }
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 304) {
    return null; // Not modified
  }

  // Store ETag for future requests
  const etag = response.headers.get('ETag');
  if (etag) {
    _etags[path] = etag;
  }

  // Phase 2: On 401, try refresh before giving up (one retry only)
  if (response.status === 401 && !_isRetry) {
    const refreshResult = await tryRefresh();
    if (refreshResult) {
      return request(path, options, true); // Retry with new token
    }
    clearToken();
    clearRefreshToken();
    throw new Error('Authentication required');
  }

  if (response.status === 401) {
    clearToken();
    clearRefreshToken();
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    const msg = error.error || error.detail || response.statusText;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }

  return response.json();
}

// ── Auth API ──────────────────────────────────────────────────────────

export const auth = {
  login: (email, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () => request('/api/auth/me'),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword, newPassword) =>
    request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),
};

// ── Dashboard API ─────────────────────────────────────────────────────

export const dashboard = {
  status: () => request('/api/dashboard/status'),
  modules: () => request('/api/dashboard/modules'),
  builds: () => request('/api/dashboard/builds'),
  logs: (lines = 60) => request(`/api/dashboard/logs?lines=${lines}`),
  providers: () => request('/api/dashboard/providers'),
  research: () => request('/api/dashboard/research'),
  settings: () => request('/api/dashboard/settings'),
  audit: (lines = 50) => request(`/api/dashboard/audit?lines=${lines}`),
  generate: (description) =>
    request('/api/dashboard/generate', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),
  cancel: () => request('/api/dashboard/cancel', { method: 'POST' }),
  killModule: (name) => request(`/api/dashboard/module/${name}`, { method: 'DELETE' }),
  apply: (buildId) =>
    request('/api/dashboard/apply', {
      method: 'POST',
      body: JSON.stringify({ build_id: buildId }),
    }),
};

// ── Fleet API ─────────────────────────────────────────────────────────

export const fleet = {
  summary: () => request('/api/fleet/summary'),
  vehicles: (status) => request(`/api/fleet/vehicles${status ? '?status=' + status : ''}`),
  create: (data) => request('/api/fleet/vehicles', { method: 'POST', body: JSON.stringify(data) }),
};

// ── Rentals API ───────────────────────────────────────────────────────

export const rentals = {
  list: (limit = 50) => request(`/api/rentals?limit=${limit}`),
  get: (id) => request(`/api/rentals/${id}`),
  create: (data) => request('/api/rentals', { method: 'POST', body: JSON.stringify(data) }),
  returnRental: (id, data) => request(`/api/rentals/${id}/return`, { method: 'POST', body: JSON.stringify(data) }),
  updateStatus: (id, data) => request(`/api/rentals/${id}/status`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// ── NARSA API ─────────────────────────────────────────────────────────

export const narsa = {
  stats: () => request('/api/narsa/stats'),
  fines: (status) => request(`/api/narsa/fines${status ? '?status=' + status : ''}`),
  fineDetail: (id) => request(`/api/narsa/fines/${id}`),
  createFine: (data) => request('/api/narsa/fines', { method: 'POST', body: JSON.stringify(data) }),
  declareFine: (id) => request(`/api/narsa/fines/${id}/declare`, { method: 'POST' }),
  matchFine: (id) => request(`/api/narsa/fines/${id}/match`, { method: 'POST' }),
  urgentFines: () => request('/api/narsa/fines/urgent'),
  declarationPacket: (id) => request(`/api/narsa/fines/${id}/declaration-packet`),
};

// ── DGSN API ─────────────────────────────────────────────────────────

export const dgsn = {
  reports: (limit = 30) => request(`/api/dgsn/reports?limit=${limit}`),
  reportByDate: (date, branchId) => request(`/api/dgsn/reports/${date}${branchId ? '?branch_id=' + branchId : ''}`),
  generate: (data) => request('/api/dgsn/reports/generate', { method: 'POST', body: JSON.stringify(data) }),
  submit: (id) => request(`/api/dgsn/reports/${id}/submit`, { method: 'POST' }),
  pending: () => request('/api/dgsn/pending'),
};

// ── Blacklist API ────────────────────────────────────────────────────

export const blacklist = {
  list: (q) => request(`/api/blacklist${q ? '?q=' + encodeURIComponent(q) : ''}`),
  stats: () => request('/api/blacklist/stats'),
  get: (id) => request(`/api/blacklist/${id}`),
  add: (data) => request('/api/blacklist', { method: 'POST', body: JSON.stringify(data) }),
  remove: (id) => request(`/api/blacklist/${id}`, { method: 'DELETE' }),
};

// ── Caisse API ───────────────────────────────────────────────────────

export const caisse = {
  payments: (date) => request(`/api/caisse/payments${date ? '?date=' + date : ''}`),
  today: () => request('/api/caisse/today'),
  record: (data) => request('/api/caisse/payments', { method: 'POST', body: JSON.stringify(data) }),
  voidPayment: (id, data) => request(`/api/caisse/payments/${id}/void`, { method: 'POST', body: JSON.stringify(data) }),
};

export default {
  auth,
  dashboard,
  fleet,
  rentals,
  narsa,
  dgsn,
  blacklist,
  caisse,
  setToken,
  getToken,
  clearToken,
  setRefreshToken,
  getRefreshToken,
  clearRefreshToken,
};
